import {
  PersonAddRounded as AddUserIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  LocationOnOutlined as LocationIcon,
  BusinessOutlined as CompanyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Tooltip,
  IconButton,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Component from "components/Component";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import {
  PERSON_TYPES,
  buildPeopleCsv,
  buildPersonAddressUpdate,
  getTypeLabel,
  peopleCsvFilename,
  seedAddressComponents,
} from "components/settings/PeopleConfig.support";
import * as labels from "config.labels";
import FileSaver from "file-saver";
import { Formik, Form, Field, FieldArray } from "formik";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import {
  useListPeopleQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
} from "services/staticDataService";
import { useTranslator } from "store/hooks/staticDataHooks";
import * as Yup from "yup";

const getTypeColor = (type) => {
  switch (type) {
    case "UNDERWRITER":
      return "primary";
    case "BROKER":
    case "PUNI_BROKER":
    case "RETAIL_BROKER":
    case "SURPLUS_BROKER":
    case "PRODUCER_BROKER":
    case "WHOLESALE_BROKER":
      return "success";
    case "ASSISTANT":
      return "secondary";
    default:
      return "default";
  }
};

const PersonCard = ({ person, onEdit, onDelete, isUpdating }) => {
  const roles = person?.roles || [];
  const metadata = person?.metadata || {};
  const company = metadata?.company || null;
  const location = metadata?.location || null;
  const telephone = metadata?.telephone || null;
  const workNumber = metadata?.workNumber || null;

  const primaryRole = roles[0] || null;
  const roleSubtitle = primaryRole ? getTypeLabel(primaryRole) : "No Role";

  return (
    <Card
      variant={"outlined"}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&:hover .action-buttons": {
          opacity: 1,
        },
      }}
    >
      <Box
        className={"action-buttons"}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 0.5,
          opacity: 0,
          transition: "opacity 0.2s",
        }}
      >
        <Tooltip title={"Edit"}>
          <IconButton
            size={"small"}
            onClick={() => onEdit(person)}
            disabled={isUpdating}
            sx={{
              backgroundColor: "background.paper",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <EditIcon fontSize={"small"} color={"primary"} />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Delete"}>
          <IconButton
            size={"small"}
            onClick={() => onDelete(person)}
            disabled={isUpdating}
            sx={{
              backgroundColor: "background.paper",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <DeleteIcon fontSize={"small"} color={"error"} />
          </IconButton>
        </Tooltip>
      </Box>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant={"h6"} sx={{ fontWeight: 600, mb: 0.5 }}>
              {person?.name}
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"}>
              {roleSubtitle}
            </Typography>
          </Box>

          <Stack spacing={1}>
            {person?.email && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {person.email}
                </Typography>
              </Stack>
            )}

            {telephone && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant={"body2"} color={"text.secondary"}>
                  {telephone}
                </Typography>
              </Stack>
            )}

            {workNumber && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant={"body2"} color={"text.secondary"}>
                  {workNumber}
                </Typography>
              </Stack>
            )}

            {location && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <LocationIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant={"body2"} color={"text.secondary"}>
                  {location}
                </Typography>
              </Stack>
            )}

            {company && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <CompanyIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {company}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>

      {roles.length > 0 && (
        <>
          <Divider />
          <CardActions sx={{ pt: 1.5, pb: 1.5, px: 2 }}>
            <Stack direction={"row"} spacing={0.5} flexWrap={"wrap"}>
              {roles.map((role) => (
                <Chip
                  key={role}
                  label={getTypeLabel(role)}
                  color={getTypeColor(role)}
                  size={"small"}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </CardActions>
        </>
      )}

      {person?.metadata?.policyUnderwriter?.name && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontWeight: 600, textTransform: "uppercase", mb: 0.5 }}
            >
              {"Policy Underwriter"}
            </Typography>
            <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
              {person.metadata.policyUnderwriter.name}
            </Typography>
            {person.metadata.policyUnderwriter.title && (
              <Typography variant={"caption"} color={"text.secondary"}>
                {person.metadata.policyUnderwriter.title}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Card>
  );
};

const PersonTable = ({ people, onEdit, onDelete, isUpdating }) => {
  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant={"body2"} color={"text.secondary"}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Stack direction={"row"} spacing={0.5} flexWrap={"wrap"}>
            {(params.value || []).map((role) => (
              <Chip
                key={role}
                label={getTypeLabel(role)}
                color={getTypeColor(role)}
                size={"small"}
              />
            ))}
          </Stack>
        </Box>
      ),
    },
    {
      field: "company",
      headerName: "Company",
      width: 150,
      valueGetter: (value, row) => row?.metadata?.company || "",
    },
    {
      field: "location",
      headerName: "Location",
      width: 150,
      valueGetter: (value, row) => row?.metadata?.location || "",
    },
    {
      field: "telephone",
      headerName: "Phone",
      width: 150,
      valueGetter: (value, row) => row?.metadata?.telephone || "",
    },
    {
      field: "workNumber",
      headerName: "Work",
      width: 150,
      valueGetter: (value, row) => row?.metadata?.workNumber || "",
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      width: 150,
      valueGetter: (value, row) => row?.metadata?.mobile_number || "",
    },
    {
      field: "address",
      headerName: "Address",
      width: 250,
      valueGetter: (value, row) => row?.metadata?.address || "",
    },
    {
      field: "licenseNumber",
      headerName: "Licenses",
      width: 180,
      valueGetter: (value, row) =>
        (row?.metadata?.licenseNumber || []).join(", "),
    },
    {
      field: "policyUnderwriter",
      headerName: "Policy Underwriter",
      width: 200,
      valueGetter: (value, row) => row?.metadata?.policyUnderwriter?.name || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            height: "100%",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={"Edit"}>
            <IconButton
              size={"small"}
              onClick={() => onEdit(params.row)}
              disabled={isUpdating}
            >
              <EditIcon fontSize={"small"} color={"primary"} />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Delete"}>
            <IconButton
              size={"small"}
              onClick={() => onDelete(params.row)}
              disabled={isUpdating}
            >
              <DeleteIcon fontSize={"small"} color={"error"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        "& .MuiDataGrid-row:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <DataGrid
        rows={people}
        columns={columns}
        autoHeight
        disableSelectionOnClick
        rowHeight={64}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{
          border: "none",
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeader": {
            bgcolor: "action.hover",
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};

const validationSchema = Yup.object({
  roles: Yup.array()
    .min(1, "At least one role is required")
    .required("At least one role is required"),
  name: Yup.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .required("Name is required"),
  email: Yup.string().trim().email("Invalid email format").nullable(),
  company: Yup.string().nullable(),
  location: Yup.string().nullable(),
  telephone: Yup.string().nullable(),
  workNumber: Yup.string().nullable(),
  mobileNumber: Yup.string().nullable(),
  addressComponents: Yup.object({
    street: Yup.string().nullable(),
    street2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    state: Yup.string().nullable(),
    zip: Yup.string().nullable(),
  }).nullable(),
  licenseNumber: Yup.array().of(Yup.string()).nullable(),
  policyUnderwriter: Yup.string().nullable(),
});

const PersonDialog = ({
  open,
  close,
  person = null,
  refetch,
  allPeople = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const translateLabel = useTranslator("uiLabels");

  const { enqueueSnackbar: queueMessage } = useSnackbar();
  const addMessage = (variant, message) => {
    queueMessage(message, {
      variant,
      autoHideDuration: 5_000,
      preventDuplicate: true,
    });
  };

  const [createPerson] = useCreatePersonMutation();
  const [updatePerson] = useUpdatePersonMutation();

  useEffect(() => {
    if (open) {
      setIsEditMode(Boolean(person));
    }
  }, [open, person]);

  const handleSubmit = async (values) => {
    setLoading(true);

    // Leaves the stored flat `address` byte-for-byte alone unless a component was
    // actually edited — see `buildPersonAddressUpdate`.
    const addressUpdate = buildPersonAddressUpdate(
      person,
      values.addressComponents
    );

    let policyUnderwriterData = null;
    if (values.policyUnderwriter) {
      const selectedUnderwriter = allPeople.find(
        (p) => p.email === values.policyUnderwriter
      );
      if (selectedUnderwriter) {
        policyUnderwriterData = {
          name: selectedUnderwriter.name,
          title: selectedUnderwriter.metadata?.title || null,
          email: selectedUnderwriter.email,
        };
      }
    }

    const personData = {
      ...(isEditMode && { id: person.id }),
      roles: values.roles,
      name: values.name.trim(),
      email: values.email?.trim() || null,
      company: values.company?.trim() || null,
      location: values.location?.trim() || null,
      telephone: values.telephone?.trim() || null,
      workNumber: values.workNumber?.trim() || null,
      mobileNumber: values.mobileNumber?.trim() || null,
      addressComponents: addressUpdate.addressComponents,
      address: addressUpdate.address,
      licenseNumber:
        values.licenseNumber
          ?.filter((num) => num?.trim())
          .map((num) => num.trim()) || null,
      policyUnderwriter: policyUnderwriterData,
    };

    try {
      const result = isEditMode
        ? await updatePerson(personData).unwrap()
        : await createPerson(personData).unwrap();

      if (result) {
        addMessage(
          "success",
          isEditMode
            ? "Person updated successfully"
            : "Person created successfully"
        );
        refetch();
        close();
      }
    } catch (error) {
      addMessage(
        "error",
        isEditMode ? "Failed to update person" : "Failed to create person"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth={"md"}>
      <DialogTitle>{isEditMode ? "Edit Person" : "Add New Person"}</DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        <Formik
          initialValues={{
            roles: person?.roles || [],
            name: person?.name || "",
            email: person?.email || "",
            company: person?.metadata?.company || "",
            location: person?.metadata?.location || "",
            telephone: person?.metadata?.telephone || "",
            workNumber: person?.metadata?.workNumber || "",
            mobileNumber: person?.metadata?.mobile_number || "",
            // Stored components win; a legacy flat string is parsed into components.
            addressComponents: seedAddressComponents(person),
            licenseNumber: person?.metadata?.licenseNumber || [],
            policyUnderwriter: person?.metadata?.policyUnderwriter?.email || "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            isValid,
            dirty,
            submitForm,
            values,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <Field name={"name"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Name"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name={"company"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Company"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.company && Boolean(errors.company)}
                        helperText={touched.company && errors.company}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"email"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Email"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        type={"email"}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <FormLabel
                    component={"legend"}
                    sx={{ mb: 1, color: "text.primary" }}
                  >
                    {"Roles (select all that apply)"}
                  </FormLabel>
                  <FormGroup>
                    <Grid container spacing={1}>
                      {PERSON_TYPES.map((type) => (
                        <Grid item xs={6} key={type.value}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.roles.includes(type.value)}
                                onChange={(e) => {
                                  const newRoles = e.target.checked
                                    ? [...values.roles, type.value]
                                    : values.roles.filter(
                                        (r) => r !== type.value
                                      );
                                  setFieldValue("roles", newRoles);
                                }}
                                disabled={loading}
                              />
                            }
                            label={type.label}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                  {touched.roles && errors.roles && (
                    <Typography
                      variant={"caption"}
                      color={"error"}
                      sx={{ mt: 0.5 }}
                    >
                      {errors.roles}
                    </Typography>
                  )}
                </Grid>
                {values.roles.includes("UNDERWRITER") && (
                  <Grid item xs={12}>
                    <Field name={"policyUnderwriter"}>
                      {({ field }) => (
                        <TextField
                          {...field}
                          label={"Policy Underwriter"}
                          variant={"outlined"}
                          fullWidth
                          disabled={loading}
                          select
                          error={
                            touched.policyUnderwriter &&
                            Boolean(errors.policyUnderwriter)
                          }
                          helperText={
                            touched.policyUnderwriter &&
                            errors.policyUnderwriter
                          }
                        >
                          <MenuItem value={""}>{"None"}</MenuItem>
                          {allPeople
                            .filter(
                              (p) =>
                                p.roles?.includes("UNDERWRITER") &&
                                p.id !== person?.id
                            )
                            .map((p) => (
                              <MenuItem key={p.email} value={p.email}>
                                {p.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    </Field>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Field name={"telephone"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Telephone"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.telephone && Boolean(errors.telephone)}
                        helperText={touched.telephone && errors.telephone}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name={"workNumber"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Work Number"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.workNumber && Boolean(errors.workNumber)}
                        helperText={touched.workNumber && errors.workNumber}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name={"mobileNumber"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Mobile Number"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={
                          touched.mobileNumber && Boolean(errors.mobileNumber)
                        }
                        helperText={touched.mobileNumber && errors.mobileNumber}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name={"location"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Location"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.location && Boolean(errors.location)}
                        helperText={touched.location && errors.location}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"addressComponents.street"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={translateLabel(
                          labels.PERSON_ADDRESS_STREET_LABEL,
                          { default: "Address Line 1" }
                        )}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"addressComponents.street2"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={translateLabel(
                          labels.PERSON_ADDRESS_STREET2_LABEL,
                          { default: "Address Line 2" }
                        )}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Field name={"addressComponents.city"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={translateLabel(
                          labels.PERSON_ADDRESS_CITY_LABEL,
                          {
                            default: "City",
                          }
                        )}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field name={"addressComponents.state"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={translateLabel(
                          labels.PERSON_ADDRESS_STATE_LABEL,
                          { default: "State" }
                        )}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Field name={"addressComponents.zip"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={translateLabel(labels.PERSON_ADDRESS_ZIP_LABEL, {
                          default: "Zip Code",
                        })}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <FieldArray name={"licenseNumber"}>
                    {({ push, remove }) => (
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant={"body2"}
                            sx={{ fontWeight: 500 }}
                          >
                            {"License Numbers"}
                          </Typography>
                          <Button
                            size={"small"}
                            startIcon={<AddIcon />}
                            onClick={() => push("")}
                            isDisabled={loading}
                          >
                            {"Add License"}
                          </Button>
                        </Box>
                        {values.licenseNumber.length > 0 ? (
                          values.licenseNumber.map((_, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "flex-start",
                              }}
                            >
                              <Field name={`licenseNumber.${index}`}>
                                {({ field }) => (
                                  <TextField
                                    {...field}
                                    label={`License ${index + 1}`}
                                    variant={"outlined"}
                                    fullWidth
                                    disabled={loading}
                                    size={"small"}
                                  />
                                )}
                              </Field>
                              <IconButton
                                onClick={() => remove(index)}
                                disabled={loading}
                                size={"small"}
                                color={"error"}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Box>
                          ))
                        ) : (
                          <Typography
                            variant={"body2"}
                            color={"text.secondary"}
                            sx={{ fontStyle: "italic" }}
                          >
                            {"No license numbers added"}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
              <DialogActions sx={{ px: 0, pt: 3 }}>
                <Button
                  onClick={close}
                  variant={"outlined"}
                  isDisabled={loading}
                >
                  {"Cancel"}
                </Button>
                <Button
                  onClick={submitForm}
                  isLoading={loading}
                  isDisabled={loading || !isValid || !dirty}
                >
                  {isEditMode ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

const PeopleConfig = () => {
  const [people, setPeople] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [selectedRole, setSelectedRole] = useState("ALL");
  const [viewMode, setViewMode] = useState("CARD");

  const { enqueueSnackbar } = useSnackbar();

  const {
    data: retrievedPeople,
    isFetching: peopleLoading,
    refetch,
  } = useListPeopleQuery();

  const [deletePerson] = useDeletePersonMutation();

  useEffect(() => {
    if (retrievedPeople) {
      setPeople(retrievedPeople);
    }
  }, [retrievedPeople]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleOpenDialog = (person = null) => {
    setEditingPerson(person);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPerson(null);
  };

  const handleDeletePerson = async (person) => {
    if (window.confirm(`Are you sure you want to delete ${person.name}?`)) {
      try {
        await deletePerson(person.id).unwrap();
        enqueueSnackbar("Person deleted successfully", {
          variant: "success",
          autoHideDuration: 5000,
        });
        refetch();
      } catch (error) {
        enqueueSnackbar("Failed to delete person", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    }
  };

  const filteredPeople = people.filter((person) => {
    const matchesSearch =
      person?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      person?.email?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      person?.metadata?.company
        ?.toLowerCase()
        ?.includes(searchText.toLowerCase()) ||
      person?.roles?.some((role) =>
        role?.toLowerCase()?.includes(searchText.toLowerCase())
      );

    const matchesRole =
      selectedRole === "ALL" || person?.roles?.includes(selectedRole);

    return matchesSearch && matchesRole;
  });

  const handleExportCsv = () => {
    const csv = buildPeopleCsv(filteredPeople);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    FileSaver.saveAs(blob, peopleCsvFilename(selectedRole));
    const count = filteredPeople.length;
    enqueueSnackbar(
      `Exported ${count} ${count === 1 ? "person" : "people"} to CSV`,
      {
        variant: "success",
        autoHideDuration: 5000,
      }
    );
  };

  return (
    <Component
      title={"Edit People"}
      loading={peopleLoading}
      options={
        <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
          <ToggleButtonGroup
            size={"small"}
            value={viewMode}
            exclusive
            onChange={(e, next) => next && setViewMode(next)}
            aria-label={"view mode"}
          >
            <ToggleButton value={"CARD"} aria-label={"card view"}>
              <CardViewIcon fontSize={"small"} />
            </ToggleButton>
            <ToggleButton value={"TABLE"} aria-label={"table view"}>
              <TableViewIcon fontSize={"small"} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            size={"small"}
            color={"secondary"}
            startIcon={<ExportIcon />}
            isDisabled={peopleLoading || filteredPeople.length === 0}
            tooltip={
              "Download the people currently shown (search and role filter applied) as a CSV"
            }
            tooltipRequired
            onClick={handleExportCsv}
          >
            {"Export CSV"}
          </Button>
          <Button
            size={"small"}
            startIcon={<AddUserIcon />}
            onClick={() => handleOpenDialog()}
          >
            {"Add Person"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <Box sx={{ flexGrow: 1 }}>
            <InlineSearch
              placeholder={"Search by name, email, or company..."}
              onChange={(text) => setSearchText(text)}
            />
          </Box>
          <TextField
            select
            size={"small"}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={"ALL"}>{"All Roles"}</MenuItem>
            {PERSON_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Typography variant={"body2"} color={"text.secondary"}>
          {`Showing ${filteredPeople.length} of ${people.length} people`}
        </Typography>

        {filteredPeople?.length > 0 ? (
          viewMode === "CARD" ? (
            <Box sx={{ mx: -1 }}>
              <Grid container spacing={2}>
                {filteredPeople.map((person) => (
                  <Grid key={person.id} item xs={12} sm={6} md={4}>
                    <PersonCard
                      person={person}
                      onEdit={handleOpenDialog}
                      onDelete={handleDeletePerson}
                      isUpdating={peopleLoading}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <PersonTable
              people={filteredPeople}
              onEdit={handleOpenDialog}
              onDelete={handleDeletePerson}
              isUpdating={peopleLoading}
            />
          )
        ) : (
          <Box
            sx={{
              height: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {peopleLoading ? (
              <DefaultLoader size={64} />
            ) : (
              <Typography>
                {searchText || selectedRole !== "ALL"
                  ? "No matches found"
                  : "No people configured"}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      <PersonDialog
        open={dialogOpen}
        close={handleCloseDialog}
        person={editingPerson}
        refetch={refetch}
        allPeople={people}
      />
    </Component>
  );
};

export default PeopleConfig;
